import { FC, useEffect, useRef } from 'react'
import { colors } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components'
import Head from 'next/head'
import { useRouter } from 'next/router'
import styled, { useTheme } from 'styled-components'
import { Text } from '@/components/common'
import Modal from '@/components/common/modals/Modal'
import SimpleModal from '@/components/common/modals/SimpleModal'
import { Typo, TypoVariant } from '@/components/common/typography'
import { Skeleton } from '@/components/common/ui/skeleton'
import { FitpetTitleHeader } from '@/components/domains/layouts/header/fitpet-title-header'
import { ENV_CONFIG } from '@/constants/env/config'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import { FontWeightEnum } from '@/constants/font-weight.enum'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { S3_FOLDERS_LEGACY } from '@/constants/legacy/constEnv'
import { LAYOUT_UNITS } from '@/constants/legacy/constLayout'
import { MembershipImageUrl } from '@/constants/membership-image-url'
import { GetMembershipOneLink } from '@/constants/membership-one-link'
import useShareLink from '@/containers/hooks/share-link.hook'
import { MembershipIntroContainer } from '@/containers/membership/MembershipIntroContainer'
import { Bridge } from '@/utils/bridge/bridge'
import appBridgeProvider, { isAbleStatusBarFontChange, isAppStorageDisabled, isInAppFlag } from '@/utils/utilBridge'
import { isDateAfter } from '@/utils/utilDate'

const PageMembershipIntroContent: FC = () => {
  const { color } = useTheme()
  const router = useRouter()

  const showWarningRef = useRef<HTMLImageElement>(null)
  const {
    showWarning,
    toggleShowWarning,
    visible,
    modalOnOk,
    handleClickBottomButton,
    getBottomButtonText,
    meIsLoading,
    modalTitle,
    modalDescription,
    invitationCode,
    showAppUpdateModal,
    setShowAppUpdateModal,
  } = MembershipIntroContainer.useContainer()
  const bodyImageUrl = `${ENV_CONFIG.s3HostUrl}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/membership_intro_body-25-04-01-1.jpg`
  const bodyImageUrlV20251201 = `${ENV_CONFIG.s3HostUrl}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/membership_intro_body-25-12-01-1.png`
  const footerImageUrl = `${ENV_CONFIG.s3HostUrl}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/membership_intro_footer-25-04-01-1.jpg`
  const footerImageUrlV20251201 = `${ENV_CONFIG.s3HostUrl}/${S3_FOLDERS_LEGACY.ASSET_IMAGES}/membership/membership_intro_footer-25-12-01-1.png`
  const appUpdateWords = '최신 버전으로 업데이트 후\n초대 받은 링크를 통해 다시 접속\n해주세요.'.split('\n')

  useEffect(() => {
    if (isInAppFlag) {
      appBridgeProvider((bridge: Bridge) => {
        if (isAbleStatusBarFontChange) {
          bridge.statusBarColorWhite({ isWhite: false })
        }
        bridge.iosBounceToggle({ isShow: true })
      })
    }
  }, [isInAppFlag])

  useEffect(() => {
    if (showWarning && showWarningRef.current) {
      window.scrollTo({
        top: showWarningRef.current.offsetTop - showWarningRef.current.offsetHeight,
        behavior: 'smooth',
      })
    }
  }, [showWarning, showWarningRef.current])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    document.body.style.touchAction = 'none'

    return () => {
      document.body.style.overflow = 'unset'
      document.body.style.touchAction = 'auto'
    }
  }, [])

  const getBodyImageUrl = () => {
    const now = new Date().toISOString()
    if (isDateAfter(now, new Date('2025-12-01').toISOString())) {
      return bodyImageUrlV20251201
    } else {
      return bodyImageUrl
    }
  }

  const getFooterImageUrl = () => {
    const now = new Date().toISOString()
    if (isDateAfter(now, new Date('2025-12-01').toISOString())) {
      return footerImageUrlV20251201
    } else {
      return footerImageUrl
    }
  }

  const handleConfirmClick = () => {
    router.back()
  }

  return (
    <StyledContainer>
      {meIsLoading ? (
        <Skeleton />
      ) : (
        <>
          <StyledImg src={getBodyImageUrl()} />
          <StyledAccordion onClick={toggleShowWarning} ref={showWarningRef}>
            <Text.Body1 fontColor={GlobalColorEnum.GrayWhite} fontWeight={FontWeightEnum.Bold}>
              유의사항
            </Text.Body1>
            <FitpetIcon icon={showWarning ? 'icChevronUp' : 'icChevronDown'} size={24} color={colors.GRAY_100} />
          </StyledAccordion>
          <StyledImg src={getFooterImageUrl()} hide={!showWarning} />
          <StyledButtonContainer $isDisabled={true}>
            <StyledBottomButton onClick={() => handleClickBottomButton()}>
              {getBottomButtonText()}
              <FitpetIcon icon="icChevronRight" size={24} color={colors.GRAY_900} />
            </StyledBottomButton>
          </StyledButtonContainer>
          <Modal open={true}>
            <Modal.Content>
              <Modal.Title>
                <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
                  서비스 운영 안내
                </Typo>
              </Modal.Title>
              <Modal.SubTitle>
                <Typo variant={TypoVariant.Body3Regular} color={color.gray700}>
                  핏펫플러스 멤버십이 7월,
                  <br />
                  더욱 새로워진 혜택으로 돌아옵니다.
                  <br />
                  현재 멤버십 신규 가입은 잠시 중단되었으며,
                  <br />
                  기존 회원 혜택은 정상 이용 가능합니다.
                  <br />
                  이용에 불편을 드려 죄송합니다.
                  <br />
                  더 풍성한 혜택과 함께 곧 다시 찾아뵙겠습니다.
                  <br />
                </Typo>
              </Modal.SubTitle>
            </Modal.Content>
            <Modal.Footer>
              <ModalButton className="ok" onClick={handleConfirmClick}>
                <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                  확인
                </Typo>
              </ModalButton>
            </Modal.Footer>
          </Modal>
        </>
      )}
      {visible && (
        <SimpleModal
          title={modalTitle}
          description={modalDescription}
          initialVisible
          onOk={modalOnOk}
          useCancel={false}
        />
      )}
      {showAppUpdateModal && (
        <SimpleModal
          title={
            invitationCode
              ? '패밀리 멤버십을 시작하려면\n앱 업데이트가 필요해요'
              : '핏펫플러스를 시작하려면\n앱 업데이트가 필요해요'
          }
          description={
            invitationCode ? (
              <StyledAppUpdateSubTitle>
                <Text.Body3>{appUpdateWords[0]}</Text.Body3>
                <StyledAppUpdateBottom>
                  <Text.Body3 fontColor={color.red500}>{appUpdateWords[1]}</Text.Body3>
                  <Text.Body3>{appUpdateWords[2]}</Text.Body3>
                </StyledAppUpdateBottom>
              </StyledAppUpdateSubTitle>
            ) : (
              '최신 버전으로 업데이트하고\n멤버십 혜택을 누려보세요.'
            )
          }
          okText="업데이트 하기"
          initialVisible
          onOk={() => {
            // 구버전 앱 업데이트
            if (isInAppFlag) {
              // 찜 목록 대응
              if (!isAppStorageDisabled) {
                appBridgeProvider((bridge: Bridge) => {
                  bridge
                    .storageSetItem('membershipInvitationCode', String(invitationCode))
                    .then((data: { status: boolean }) => {
                      if (data.status) {
                        appBridgeProvider((bridge: Bridge) => bridge.goAppUpdateAction())
                      } else {
                        console.error('Fail : updateNonLoginCartProductOptions')
                      }
                    })
                })
                return
              }
              appBridgeProvider((bridge: Bridge) => bridge.goAppUpdateAction())
            }
          }}
          onAfterClose={() => {
            setShowAppUpdateModal(false)
          }}
        />
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  padding-bottom: calc(8rem + env(safe-area-inset-bottom));
  background-color: ${({ theme: { color } }) => color.membershipIntro};
`

const StyledImg = styled.img<{ hide?: boolean }>`
  width: ${({ theme: { spacing } }) => spacing.per100};
  display: ${({ hide }) => (hide ? 'none' : 'initial')};
`

const StyledAccordion = styled.div`
  padding: 1.5rem 1.6rem 1.5rem;
  background-color: ${({ theme: { color } }) => color.membershipIntro};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledAppUpdateSubTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const StyledAppUpdateBottom = styled.div`
  display: flex;
`

const StyledButtonContainer = styled.div<{ $isDisabled?: boolean }>`
  position: fixed;
  bottom: 0;
  width: ${({ theme }) => theme.spacing.per100};
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  background-color: ${({ $isDisabled, theme }) => ($isDisabled ? theme.color.gray500 : '#ff8402')};
  padding-bottom: env(safe-area-inset-bottom);
`

const StyledBottomButton = styled.button`
  width: ${({ theme: { spacing } }) => spacing.per100};
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  height: ${({ theme: { spacing } }) => spacing.size80};
  color: ${({ theme: { color } }) => color.gray900};
  gap: 0.5rem;
  background-color: transparent;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.bold};
  font-size: ${({ theme: { fontSize } }) => fontSize.h4};
  line-height: ${({ theme: { lineHeight } }) => lineHeight.h6};

  &:disabled {
    cursor: not-allowed;
  }
`

const ModalButton = styled.button`
  flex: 1;
  border-radius: 0.8rem;
  border: 0.1rem solid;
  padding: 1.3rem 1.6rem;
  cursor: pointer;
  width: 100%;

  &.ok {
    border-color: ${({ theme }) => theme.color.blue500};
    background-color: ${({ theme }) => theme.color.blue500};
  }
`

const PageMembershipIntro = () => {
  const { processShareLink } = useShareLink()

  const shareLink = () => {
    processShareLink(GetMembershipOneLink(), {
      appBridgeData: {
        title: '핏펫플러스 멤버십',
        eventName: FitpetMallEventEnum.ClickEventsaleSharetypeButton,
        eventProperty: {},
      },
    })
  }

  return (
    <>
      <Head>
        <meta property="og:type" content="website" key="type" />
        <meta property="og:title" content="핏펫플러스 멤버십 무료 초대장" key="title" />
        <meta property="og:image" content={MembershipImageUrl.OgImageUrl} key="image" />
        <meta property="og:description" content="패밀리 멤버 초대장이 도착했어요!" key="description" />
      </Head>
      <MembershipIntroContainer.Provider>
        <FitpetTitleHeader
          title="핏펫플러스"
          rightNode={
            <StyledButton onClick={shareLink}>
              <FitpetIcon icon="icShare" size={24} color={colors.GRAY_900} />
            </StyledButton>
          }
        />
        <PageMembershipIntroContent />
      </MembershipIntroContainer.Provider>
    </>
  )
}

export default PageMembershipIntro

const StyledButton = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`
